import { createFileRoute, Link } from '@tanstack/react-router'
import { HelpLayout } from '@/components/help-center/help-layout'
import { HelpArticleContent } from '@/components/help-center/help-article'

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Import contacts from Android',
  description: 'Select contacts from Android using the Contact Picker or upload a CSV.',
  step: [
    { '@type': 'HowToStep', text: 'Open MessageDrafts in Chrome on Android.' },
    { '@type': 'HowToStep', text: 'Tap Import from Phone and grant contact access.' },
    { '@type': 'HowToStep', text: 'Select contacts and confirm the import.' },
  ],
}

export const Route = createFileRoute('/_public/app/help/import-android')({
  component: ImportAndroidPage,
  head: () => ({
    meta: [
      { title: 'Import Contacts from Android | MessageDrafts Help' },
      {
        name: 'description',
        content:
          'Learn how to import contacts from Android using the Contact Picker or CSV upload.',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(howToSchema),
      },
    ],
  }),
})

function ImportAndroidPage() {
  return (
    <HelpLayout
      title="Import contacts from Android"
      description="Select contacts quickly with the Android Contact Picker or upload a CSV file."
    >
      <HelpArticleContent
        slug="import-android"
        title="Overview"
        description="This guide covers Android contact import options and CSV formatting tips."
        tags={['android', 'import', 'contacts', 'csv']}
        devices={['android']}
      >
        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Prerequisites</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>Android 11 or later.</li>
            <li>Chrome, Samsung Internet, or another browser with Contact Picker support.</li>
            <li>HTTPS connection to MessageDrafts.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Step-by-step</h3>
          <ol className="list-decimal pl-5 space-y-2">
            <li>Open MessageDrafts in your Android browser.</li>
            <li>
              Tap <strong>Import from Phone</strong> and grant contact access.
            </li>
            <li>Select the contacts you want to include.</li>
            <li>Confirm to move to the next step.</li>
          </ol>
        </section>

        <figure className="space-y-2">
          <img
            src="/help/android-contact-picker.svg"
            alt="Android contact picker showing a list of contacts to select"
            className="w-full rounded-xl border border-slate-200"
          />
          <figcaption className="text-xs text-slate-500">
            Choose contacts and confirm access.
          </figcaption>
        </figure>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">CSV upload tips</h3>
          <p>
            If your browser does not support the Contact Picker, export a CSV from Google
            Contacts and upload it. Your CSV must include headers like <strong>Name</strong>,
            <strong> Phone</strong>, and/or <strong>Email</strong>.
          </p>
          <figure className="space-y-2">
            <img
              src="/help/csv-upload.svg"
              alt="CSV upload area with supported column headers"
              className="w-full rounded-xl border border-slate-200"
            />
            <figcaption className="text-xs text-slate-500">
              CSV upload works across all devices.
            </figcaption>
          </figure>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Troubleshooting</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>Contact picker blocked:</strong> Check browser permissions for contacts.
            </li>
            <li>
              <strong>CSV rejected:</strong> Ensure the first row contains headers (Name, Phone,
              Email).
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Next steps</h3>
          <p>
            After importing, you can draft messages and send them from your default SMS app.
            Review our <Link to="/app/help/faq" className="underline">
              FAQ
            </Link>{' '}
            for common questions.
          </p>
        </section>
      </HelpArticleContent>
    </HelpLayout>
  )
}
